/**
 * Provides a hook to manage a string value in sessionStorage.
 * 
 * Does not manage JSON values nor localStorage. Use {@link useStorageJSON} for that.
 * 
 * @packageDocumentation
 * @module useSessionStorageString
 * @public
 */

// TODO: make it a thin wrapper around useStorageString and deprecate this one

import { useCallback, useEffect, useState } from 'react';

export function useSessionStorageString(
  key: string,
  initialValue: string
): [string, (newValue: string) => void] {
  // -- getter and setter

  const getStoredValue = (): string => {
    const stored = sessionStorage.getItem(key);
    return stored !== null ? stored : initialValue;
  };

  const [value, setValue] = useState<string>(getStoredValue);

  const setStoredValue = useCallback(
    (newValue: string) => {
      const oldValue = value;
      setValue(newValue);
      sessionStorage.setItem(key, newValue);

      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          newValue,
          oldValue,
          storageArea: sessionStorage,
        })
      );
    },
    [key, value]
  );

  // -- detect changes made by the hook

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea === sessionStorage && event.key === key) {
        setValue(event.newValue ?? initialValue);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [key, initialValue]);

  // -- detect change made by direct sessionStorage API usage

  useEffect(() => {
    let lastValue = sessionStorage.getItem(key) ?? initialValue;

    const interval = setInterval(() => {
      const currentValue = sessionStorage.getItem(key) ?? initialValue;
      if (currentValue !== lastValue) {
        lastValue = currentValue;
        setValue(currentValue);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [key, initialValue]);

  return [value, setStoredValue];
}
